/**
 * overlayInsertion.ts
 *
 * Shared "Add Overlay Text & Graphic from a transcript selection" sequence used
 * by every surface that inserts a composition / overlay bound to a highlighted
 * transcript range (Creator Studio left transcript hover menu + Content Studio
 * clip-preview transcript). Centralising it keeps the surfaces from drifting and
 * puts the "why" in one place (PLAT-10105 origin / PLAT-10213 Creator Studio).
 *
 * The sequence each surface needs, identically:
 *  1. `overlay_added_from_transcript_started` analytics (with the surface).
 *  2. `createCompositionElement` with a `timeRange` override so the overlay is
 *     bound to the exact highlighted `[startTime, endTime]` with NO ripple
 *     (sits on top of existing content like an added photo).
 *  3. On success: select + activate the new element, repaint immediately (the
 *     command's markDirty sync is debounced ~300ms), persist the template meta,
 *     and fire `..._completed`.
 *  4. On failure: log, surface a user-facing error (no silent failure), and fire
 *     `..._failed`.
 *
 * The only per-surface differences are the `surface` analytics tag, the
 * video-like object, and which `refreshSource` to call — all injected via ctx.
 */

import {
  type CompositionQuickEditSurface,
  trackOverlayAddedFromTranscriptCompleted,
  trackOverlayAddedFromTranscriptFailed,
  trackOverlayAddedFromTranscriptStarted,
} from '@src/analytics/EventTracker';
import { createCompositionElement } from '@src/services/composition/CompositionElementService';
import { extractErrorInfo } from '@src/utility/error';
import {
  storeCompositionTemplateMetadata,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { buildOverlayMarker } from '@src/utility/overlayMarker';

import type {
  AITextControlConfiguration,
  CompositionTemplate,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { Showcase, Story } from '@src/types.ts/story';
import type { ExtendedAspectRatio } from '@src/types.ts/video';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { VideoLike } from '@src/utility/extraElementData';

/** Highlighted transcript range the overlay is bound to. */
export interface OverlayTranscriptRange {
  startTime: number;
  endTime: number;
  /** The highlighted text (carried for callers; not used by the insert). */
  text?: string;
  /**
   * Stable source `initial_index` of the first/last highlighted word. When
   * present, the anchor is stored in extraElementData (`transcriptAnchor`) so
   * the overlay's transcript underline maps to these words through later edits
   * — see `findOverlayRanges`. Absent → the underline uses the fixed time
   * window.
   */
  anchorStartInitialIndex?: number;
  anchorEndInitialIndex?: number;
}

/**
 * Minimal video-like surface the insert needs. Satisfied by both
 * `VideoCreatorStore` (Creator Studio) and `ClipVideoCreatorAdapter`
 * (clip preview).
 */
export interface OverlayInsertVideoLike {
  storyId?: string | null;
  duration: number;
  /** Live current video — always called, never cached (service contract). */
  getCurrentVideo: () => VideoLike;
  getCurrentAspectRatio: () => ExtendedAspectRatio;
  organization?: Showcase;
  story?: Story;
  convertToVh: (value: string) => string;
  setActiveElements: (id: string) => void | Promise<void>;
  /** Move the playhead (so the just-added overlay is visible on the canvas). */
  setTime: (time: number) => void | Promise<void>;
}

export interface AddOverlayFromTranscriptContext {
  template: CompositionTemplate;
  range: OverlayTranscriptRange;
  surface: CompositionQuickEditSurface;
  editorStore: EditorStoreLike;
  video: OverlayInsertVideoLike;
  /** Immediate canvas/iframe repaint (the command's markDirty sync is debounced). */
  refreshSource: () => void;
  /** Show a user-facing error (e.g. a toast) — no silent failure. */
  onError: (message: string) => void;
  /**
   * PLAT-10915 — the automatic first generation for the `aiText` controls the
   * inserted overlay declares. The overlay is handed over as the marker
   * `findOverlayRanges` maps from, built from the highlighted range the insert
   * was bound to: the real marker list is rebuilt on the next render, and the
   * generation is fired before that.
   *
   * Omit and nothing generates — the overlay lands with its placeholder,
   * exactly as it did before this ticket.
   */
  onAITextControlsCreated?: (params: {
    elementId: string;
    controls: ReadonlyArray<AITextControlConfiguration>;
    overlay: TimelineMarker;
  }) => void;
  logger: {
    error: (message: string, context?: Record<string, unknown>) => void;
  };
}

const ADD_OVERLAY_ERROR_MESSAGE = 'Failed to add overlay graphic';

/**
 * Insert an overlay bound to a transcript selection, with analytics + repaint +
 * error handling. Returns whether the overlay was added (callers can gate on it).
 */
export async function addOverlayFromTranscript(
  ctx: AddOverlayFromTranscriptContext,
): Promise<boolean> {
  const { template, range, surface, editorStore, video, refreshSource } = ctx;
  const storyId = video.storyId ?? undefined;

  trackOverlayAddedFromTranscriptStarted({
    story_id: storyId,
    surface,
    time_start: range.startTime,
    time_end: range.endTime,
  });

  try {
    const result = await createCompositionElement({
      template,
      editorStore,
      videoProps: {
        duration: video.duration,
        getCurrentVideo: video.getCurrentVideo,
        aspectRatio: video.getCurrentAspectRatio(),
        time: range.startTime,
        organization: video.organization,
        story: video.story,
        convertToVh: value => video.convertToVh(value),
      },
      // PLAT-10105 — the highlighted range, no ripple.
      //
      // PLAT-10921 step 1 — this is now the DEFAULT, not an override: the
      // service consults `template.placement` first and only falls back to this
      // range when the placement binds nothing (`current_time` / `section`) or
      // when the placement IS the selection. The anchor below is written either
      // way, so the graphic knows the passage it was placed for whichever
      // placement won.
      timeRange: {
        time: range.startTime,
        duration: range.endTime - range.startTime,
      },
      upsertExtraElementDataCb: (elementId, tpl) => {
        storeCompositionTemplateMetadata(
          video.getCurrentVideo(),
          elementId,
          tpl,
        );
        // Store the transcript anchor HERE — during creation, synchronously
        // before the element-add re-render — so the timeline-marker rebuild
        // picks it up on its first pass and `findOverlayRanges` uses the anchor
        // immediately. Writing it after `createCompositionElement` returns
        // (post-render) left the marker without an anchor until a save+reload
        // rebuilt markers from persisted extraElementData. See findOverlayRanges.
        if (
          range.anchorStartInitialIndex != null &&
          range.anchorEndInitialIndex != null
        ) {
          upsertElementMetaObject(video.getCurrentVideo(), elementId, {
            transcriptAnchor: {
              startInitialIndex: range.anchorStartInitialIndex,
              endInitialIndex: range.anchorEndInitialIndex,
            },
          });
        }
      },
      onAITextControlsCreated: (elementId, controls) => {
        // PLAT-10921 DS13/DS14 — the element already exists here, so read the
        // range it actually LANDED on rather than the selection this insert came
        // from: a clip-boundary placement resolves to a wider one. Together with
        // `placement` below that keeps the first generation's Selection scope on
        // exactly the words the transcript underlines (E4).
        const created = editorStore.editor.timeline.getElement(elementId);
        return ctx.onAITextControlsCreated?.({
          elementId,
          controls,
          overlay: buildOverlayMarker({
            id: elementId,
            time: created?.time ?? range.startTime,
            endTime: created?.end_time ?? range.endTime,
            anchor: {
              startInitialIndex: range.anchorStartInitialIndex,
              endInitialIndex: range.anchorEndInitialIndex,
            },
            placement: template.placement,
          }),
        });
      },
    });

    if (!result.ok) {
      throw new Error(result.error.message);
    }

    const { elementId } = result.value;
    if (elementId) {
      editorStore.selectElement(elementId);
      await video.setActiveElements(elementId);
    }
    // Move the playhead to the MIDDLE of the highlighted range so the overlay
    // (time-bound to `[startTime, endTime]`) is on-screen immediately — landing
    // at the start can sit right on the overlay's in-point where it isn't yet
    // visible. The midpoint is always inside the overlay's lifespan.
    await video.setTime(
      range.startTime + (range.endTime - range.startTime) / 2,
    );
    // Repaint now — the command's markDirty sync is debounced; the PLAT-10045
    // underline then picks up the new overlay's range.
    refreshSource();

    trackOverlayAddedFromTranscriptCompleted({
      story_id: storyId,
      surface,
      composition_id: template.id,
      time_start: range.startTime,
      time_end: range.endTime,
    });
    return true;
  } catch (error) {
    ctx.logger.error('Failed to add overlay graphic from transcript', {
      templateId: template.id,
      surface,
      ...extractErrorInfo(error),
    });
    // User-facing error — no silent failure.
    ctx.onError(ADD_OVERLAY_ERROR_MESSAGE);
    trackOverlayAddedFromTranscriptFailed({
      story_id: storyId,
      surface,
      composition_id: template.id,
      error_message: extractErrorInfo(error).errorMessage,
    });
    return false;
  }
}
